import type { GrammarSummary } from "@/lib/search";

/**
 * 品詞 in Japanese (日本-519) — and it **fails open**.
 *
 * `surfaces/setup/jobText.ts`'s precedent and its exact reason: the part-of-speech string is
 * **stored data**, compiled into `dictionary.sqlite` from DoJG's own deck. Translating it at the
 * source would put this repo out of step with its source and with any future re-extraction, so the
 * Japanese is a display map and an unmapped value renders as itself, in English.
 *
 * That is not defensiveness. Measured over the corpus: **40 distinct heads and 12 qualifiers**, with
 * a long tail of one-offs (`Strength` occurs once) and two typos in the source itself
 * (`Noun Modifer`, `Inflection Ending`). A table that had to be exhaustive would be wrong the first
 * time the dataset moved; one that falls through shows the English and keeps working.
 *
 * **TS only, with no server twin.** Nothing on the server renders a part of speech — `/api/grammar`
 * serves the raw string — so unlike `engine/grammar_keys.py` or `engine/markup.py` there is no
 * second implementation for this to drift against.
 *
 * ---
 *
 * **The one place the Japanese is more precise than the English**, and it is worth knowing before
 * anyone "corrects" it: DoJG writes both `Auxiliary` and `Auxiliary Verb`, and Japanese grammar
 * distinguishes them properly — 助動詞 attaches to an inflected form (だろう, た, ます) while
 * 補助動詞 is a verb used auxiliarily (〜てしまう, 〜ておく, 〜ている). So the two map to different
 * words rather than colliding, and `Auxiliary Adjective` follows as 補助形容詞.
 *
 * Verb groups take the standard Japanese conjugation classes rather than DoJG's numbering: Group 1
 * is 五段 and Group 2 is 一段.
 */

/** The head, before any parenthesised qualifier. */
const HEAD: Record<string, string> = {
  Phrase: "句",
  Conjunction: "接続詞",
  Particle: "助詞",
  Adverb: "副詞",
  "Compound Particle": "複合助詞",
  Structure: "構文",
  "Auxiliary Verb": "補助動詞",
  Verb: "動詞",
  Noun: "名詞",
  Suffix: "接尾辞",
  Auxiliary: "助動詞",
  "Auxiliary Adjective": "補助形容詞",
  "Auxiliary Noun": "補助名詞",
  Adjective: "形容詞",
  Interjection: "感動詞",
  Pronoun: "代名詞",
  "Noun Modifier": "連体修飾語",
  // The source's own typo, mapped rather than corrected — `data/grammar/README.md`'s rule is that
  // source typos are preserved so this repo stays in step with its source.
  "Noun Modifer": "連体修飾語",
  "Adverbial Phrase": "副詞句",
  Prefix: "接頭辞",
  Infix: "接中辞",
  Nominaliser: "名詞化辞",
  "て Form": "て形",
  "Demonstrative Adjective": "指示形容詞",
  "Interrogative Word": "疑問詞",
  Copula: "コピュラ",
  "Inflectional Ending": "活用語尾",
  "Inflection Ending": "活用語尾",
};

/** What appears inside the brackets. A closed set of 12 across the corpus. */
const QUALIFIER: Record<string, string> = {
  "Group 1": "五段",
  "Group 2": "一段",
  Irregular: "不規則",
  "Imperative Form": "命令形",
  "い": "い",
  "な": "な",
  "usually used in formal writing and formal speech": "主に書き言葉・改まった話し言葉",
  "used in writing and formal speech only": "書き言葉・改まった話し言葉のみ",
  "usually used in conversation": "主に話し言葉",
  "used in conversation only": "話し言葉のみ",
};

/**
 * The head alone, in Japanese — what a table-of-contents row shows and what the 品詞 filter keys on.
 *
 * A slashed head (`Adverb/Conjunction`, `Compound Particle/Conjunction`) is **derived** by mapping
 * each part rather than tabled as a pair: there are eight such combinations in the corpus and
 * tabling them would be eight more rows to keep in step for no extra information.
 */
export function posHead(source: string | null | undefined): string {
  if (!source) return "";
  const head = source.replace(/\s*[（(].*$/, "").trim();
  if (!head) return "";
  return head
    .split("/")
    .map((part) => {
      const key = part.trim();
      return HEAD[key] ?? key;
    })
    .join("・");
}

/**
 * The head plus every qualifier — what an expanded entry shows.
 *
 * Unmapped parts survive in English on either side, independently: an unknown head with a known
 * qualifier still gets the qualifier translated, and vice versa. That is the fail-open rule applied
 * per component rather than to the whole string, so one unrecognised value cannot make the rest
 * unreadable.
 */
export function posFull(source: string | null | undefined): string {
  if (!source) return "";
  const head = posHead(source);
  const quals = [...source.matchAll(/[（(]([^)）]*)[)）]/g)].map((m) => {
    const key = (m[1] ?? "").trim();
    return QUALIFIER[key] ?? key;
  });
  if (quals.length === 0) return head;
  return `${head}（${quals.join("・")}）`;
}

/**
 * Whether the full string says more than the head does.
 *
 * The row line already carries the head, so an entry whose 品詞 is a bare `Phrase` would repeat
 * itself — and **166 of the 629 entries are exactly that**. The expanded view asks this rather than
 * drawing the row unconditionally.
 */
export function posAddsDetail(source: string | null | undefined): boolean {
  if (!source) return false;
  return posFull(source) !== posHead(source);
}

/**
 * Every 品詞 head present in a set of entries, in the order the FILTER should offer them.
 *
 * Derived from the data rather than listed, because the tail is long and uneven: the top eight
 * heads cover 81% of the corpus and the rest are one- and two-entry oddities. Sorted by how many
 * entries carry each, so the filter's first options are the ones that narrow it usefully.
 *
 * Keyed on the SOURCE head, not the Japanese: the value goes in the URL (`grammar.pos`) and has to
 * survive a display map changing its mind about a word.
 */
export function posFacets(entries: readonly GrammarSummary[]): { source: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    const head = (entry.partOfSpeech ?? "").replace(/\s*[（(].*$/, "").trim();
    if (!head) continue;
    counts.set(head, (counts.get(head) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source));
}
